import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import {
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from '@material-ui/core';
import { ArrowBack } from '@material-ui/icons';
import { BarthelRecord } from '../../../constants/Records';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(3),
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: theme.spacing(2),
    },
    breadcrumb: {
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(1),
    },
    backButton: {
      marginRight: theme.spacing(2),
    },
    form: {
      padding: theme.spacing(3),
    },
    field: {
      marginBottom: theme.spacing(2),
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: theme.spacing(2),
      marginTop: theme.spacing(3),
    },
    totalScore: {
      marginTop: theme.spacing(2),
      padding: theme.spacing(2),
      backgroundColor: theme.palette.grey[100],
      borderRadius: theme.shape.borderRadius,
    },
  }),
);

const BARTHEL_ITEMS = [
  { key: 'feeding', label: '食事', options: [0, 5, 10] },
  { key: 'transfers', label: '移乗', options: [0, 5, 10, 15] },
  { key: 'grooming', label: '整容', options: [0, 5] },
  { key: 'toilet_use', label: 'トイレ動作', options: [0, 5, 10] },
  { key: 'bathing', label: '入浴', options: [0, 5] },
  { key: 'mobility', label: '歩行', options: [0, 5, 10, 15] },
  { key: 'stairs', label: '階段昇降', options: [0, 5, 10] },
  { key: 'dressing', label: '着替え', options: [0, 5, 10] },
  { key: 'bowels', label: '排便コントロール', options: [0, 5, 10] },
  { key: 'bladder', label: '排尿コントロール', options: [0, 5, 10] },
];

interface Props {
  institutionId: string;
  institutionName: string;
  userId: string;
  userName: string;
  uniqueId: string;
  currentRecord: BarthelRecord | null;
  isEdit: boolean;
  executing: boolean;
  created: boolean;
  updated: boolean;
  onFetchRecord: (uniqueId: string, recordTime: string) => void;
  onCreate: (uniqueId: string, data: Partial<BarthelRecord>) => void;
  onUpdate: (uniqueId: string, recordTime: string, data: Partial<BarthelRecord>) => void;
  onResetFlags: () => void;
}

const BarthelRecordForm: React.FC<Props> = ({
  institutionId,
  institutionName,
  userId,
  userName,
  uniqueId,
  currentRecord,
  isEdit,
  executing,
  created,
  updated,
  onCreate,
  onUpdate,
  onResetFlags,
}) => {
  const classes = useStyles();
  const history = useHistory();

  const [recordTime, setRecordTime] = useState('');
  const [scores, setScores] = useState<Record<string, number>>({});

  useEffect(() => {
    if (currentRecord && isEdit) {
      setRecordTime(currentRecord.record_time || '');
      const newScores: Record<string, number> = {};
      BARTHEL_ITEMS.forEach(item => {
        const value = currentRecord[item.key as keyof BarthelRecord];
        // APIから文字列で返ってくる可能性があるため、数値に変換
        if (value !== undefined && value !== null) {
          const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
          if (typeof numValue === 'number' && !isNaN(numValue)) {
            newScores[item.key] = numValue;
          }
        }
      });
      setScores(newScores);
    }
  }, [currentRecord, isEdit]);

  useEffect(() => {
    if (created || updated) {
      onResetFlags();
      history.push(`/institutions/${institutionId}/users/${userId}/records/barthel`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [created, updated]);

  const handleBack = () => {
    history.push(`/institutions/${institutionId}/users/${userId}/records/barthel`);
  };

  const handleScoreChange = (key: string, value: number) => {
    // 整数として保存（DynamoDB Decimal対応）
    setScores(prev => ({ ...prev, [key]: Math.floor(value) }));
  };

  const calculateTotalScore = () => {
    return Object.values(scores).reduce((sum, score) => sum + (score || 0), 0);
  };

  // 全ての必須フィールドが入力されているかチェック
  const isAllFieldsFilled = () => {
    return BARTHEL_ITEMS.every(item => scores[item.key] !== undefined && scores[item.key] !== null);
  };

  // 送信可能かどうか
  const canSubmit = () => {
    if (executing) return false;
    if (!isEdit && !recordTime) return false;
    if (!isAllFieldsFilled()) return false;
    return true;
  };

  const handleSubmit = () => {
    // スコアを文字列に変換（API仕様に準拠）
    const strScores: Record<string, string> = {};
    Object.entries(scores).forEach(([key, value]) => {
      strScores[key] = String(Math.floor(value));
    });

    if (isEdit && currentRecord) {
      // PUT: スコアのみ送信
      onUpdate(uniqueId, currentRecord.record_time, strScores as unknown as Partial<BarthelRecord>);
    } else {
      // POST: unique_idとrecord_timeも必要（全て文字列）
      const data = {
        record_time: recordTime,
        ...strScores,
      };
      onCreate(uniqueId, data as unknown as Partial<BarthelRecord>);
    }
  };

  return (
    <div className={classes.root}>
      <Typography variant="body2" className={classes.breadcrumb}>
        {institutionName} &gt; {userName} &gt; バーセルインデックス &gt; {isEdit ? '編集' : '新規作成'}
      </Typography>

      <div className={classes.header}>
        <IconButton className={classes.backButton} onClick={handleBack}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h5">{isEdit ? 'バーセルインデックス編集' : 'バーセルインデックス作成'}</Typography>
      </div>

      <Paper className={classes.form}>
        <TextField
          label="記録日"
          type="date"
          value={recordTime}
          onChange={e => setRecordTime(e.target.value)}
          fullWidth
          className={classes.field}
          InputLabelProps={{ shrink: true }}
          disabled={isEdit}
        />

        <Grid container spacing={2}>
          {BARTHEL_ITEMS.map(item => (
            <Grid item xs={12} sm={6} key={item.key}>
              <FormControl fullWidth className={classes.field}>
                <InputLabel>{item.label}</InputLabel>
                <Select
                  value={scores[item.key] ?? ''}
                  onChange={e => handleScoreChange(item.key, e.target.value as number)}
                >
                  <MenuItem value="">
                    <em>未選択</em>
                  </MenuItem>
                  {item.options.map(opt => (
                    <MenuItem key={opt} value={opt}>
                      {opt}点
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          ))}
        </Grid>

        <div className={classes.totalScore}>
          <Typography variant="h6">合計スコア: {calculateTotalScore()}点</Typography>
          {!isAllFieldsFilled() && (
            <Typography variant="body2" color="error" style={{ marginTop: 8 }}>
              ※ 全ての項目を選択してください
            </Typography>
          )}
        </div>

        <div className={classes.buttonContainer}>
          <Button variant="outlined" onClick={handleBack} disabled={executing}>
            キャンセル
          </Button>
          <Button variant="contained" color="primary" onClick={handleSubmit} disabled={!canSubmit()}>
            {executing ? <CircularProgress size={24} /> : isEdit ? '更新' : '作成'}
          </Button>
        </div>
      </Paper>
    </div>
  );
};

export default BarthelRecordForm;
